import React, { useEffect } from 'react'
import useBreakpoint from 'hooks/useBreakPoint'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/base_components/ui/dialog'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from '@/base_components/ui/drawer'
import { cn } from '@/lib/utils'
import { ReactNode } from 'react'

type Breakpoint =
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | 'custom'
  | 'mobile'
  /** Drawer for viewports at or below XLG (~1200px); dialog only on widest desktop. */
  | 'max-xl'

interface ResponsiveDialogDrawerProps {
  open: boolean
  onOpenChange?: (open: boolean) => void
  onAfterOpen?: () => void
  onOpenAutoFocus?: (e: Event) => void
  onInteractOutside?: (e: Event) => void
  onPointerDownOutside?: (e: Event) => void
  onEscapeKeyDown?: (e: Event) => void
  title?: ReactNode
  description?: ReactNode
  children: ReactNode
  drawerBreakpoint?: Breakpoint
  drawerCloseIconVisible?: boolean
  drawerDismissible?: boolean
  repositionInputs?: boolean
  classNames?: {
    dialogHeader?: string
    dialogContent?: string
    dialogClose?: string
    drawerHeader?: string
    drawerContent?: string
    drawerContentWrapper?: string
    drawerHandle?: string
    /** Close button on drawer (when `drawerCloseIconVisible`). */
    drawerClose?: string
    overlayClassName?: string
    /** Merged with overlay for drawer only (does not affect dialog). */
    drawerOverlayClassName?: string
    /**
     * Fixed strip between dimmed overlay and sheet; use to paint white behind
     * rounded top corners (transparent outside the radius).
     */
    drawerTopFill?: string
    dialogContentWrapper?: string
  }
  /** Passed to Vaul `Drawer` root; default matches vaul. */
  drawerShouldScaleBackground?: boolean
}

/**
 * A responsive component that renders either a dialog or a drawer
 * depending on the screen size.
 *
 * @component
 * @example
 * return (
 *   <ResponsiveDialogDrawer
 *     open={true}
 *     onOpenChange={() => console.log('Dialog toggled')}
 *     title="Example"
 *     description="This is an example"
 *   >
 *     <p>Child content goes here</p>
 *   </ResponsiveDialogDrawer>
 * )
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls whether the dialog or drawer is open
 * @param {(open: boolean) => void} [props.onOpenChange] - Callback when open state changes; receives the next open value
 * @param {() => void} props.onAfterOpen - Callback when dialog/drawer opens
 * @param {ReactNode} [props.title] - Optional title to display at the top of the dialog and drawer
 * @param {ReactNode} [props.description] - Optional description to display below the title of the dialog and drawer
 * @param {ReactNode} props.children - Content inside the dialog/drawer
 * @param {'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'custom' | 'mobile'} [props.drawerBreakpoint='mobile'] - Screen size breakpoint to switch to a drawer
 * @param {boolean} props.repositionInputs - Optional props that repositions inputs rather than scroll then into view if the keyboard is in the way
 * @param {Object} [props.classNames] - Optional class names for styling
 * @param {string} [props.classNames.dialogHeader] - Class names for styling dialog header
 * @param {string} [props.classNames.dialogContent] - Class names for styling the dialog content
 * @param {string} [props.classNames.drawerHeader] - Class names for styling the drawer header
 * @param {string} [props.classNames.drawerContent] - Class names for styling the drawer content
 * @param {string} [props.classNames.drawerContentWrapper] - Class names for styling the drawer content wrapper
 *
 * @returns {JSX.Element} A drawer on small screens or a dialog on large screens.
 */

export function ResponsiveDialogDrawer({
  open,
  onOpenChange,
  onAfterOpen,
  onOpenAutoFocus,
  onInteractOutside,
  onPointerDownOutside,
  onEscapeKeyDown,
  title,
  description,
  children,
  drawerBreakpoint = 'mobile',
  drawerCloseIconVisible,
  drawerDismissible = true,
  repositionInputs = true,
  classNames = {
    drawerContentWrapper: '~overflow-y-auto ~grow ~px-6 ~pb-6',
  },
  drawerShouldScaleBackground = true,
}: ResponsiveDialogDrawerProps): JSX.Element {
  const breakpoints = useBreakpoint()

  const breakpointMap: Record<Breakpoint, boolean> = {
    sm: breakpoints.isSmallSize,
    md: breakpoints.isMediumSize,
    lg: breakpoints.isLargeSize,
    xl: breakpoints.isExtraLargeSize,
    '2xl': breakpoints.isAboveXLSize,
    custom: breakpoints.isCustomSize,
    mobile: breakpoints.isMobileScreen,
    'max-xl': !breakpoints.isAboveXLSize,
  }

  useEffect(() => {
    if (open) {
      onAfterOpen && onAfterOpen()
    }
  }, [open])

  const isDrawerBreakpoint = breakpointMap[drawerBreakpoint]

  if (isDrawerBreakpoint) {
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        repositionInputs={repositionInputs}
        dismissible={drawerDismissible}
        shouldScaleBackground={drawerShouldScaleBackground}
      >
        <DrawerContent
          className={cn(
            '~h-[90dvh] ~max-h-[90dvh] ~flex ~flex-col ~min-h-0 ~overflow-hidden ~outline-none ~border-0',
            classNames.drawerContent
          )}
          drawerHandleClassName={cn(classNames.drawerHandle)}
          overlayClassName={cn(
            '~z-[1090]',
            classNames.overlayClassName,
            classNames.drawerOverlayClassName
          )}
          topFillClassName={classNames.drawerTopFill}
          onInteractOutside={onInteractOutside}
          onPointerDownOutside={onPointerDownOutside}
          onOpenAutoFocus={onOpenAutoFocus}
          onEscapeKeyDown={onEscapeKeyDown}
          closeIconVisible={drawerCloseIconVisible}
          closeClassName={cn(classNames.drawerClose)}
        >
          <div className="~flex ~flex-col ~flex-1 ~min-h-0 ~overflow-hidden">
            {title ? (
              <DrawerHeader
                className={cn(
                  '~flex ~flex-col ~items-start ~shrink-0',
                  classNames.drawerHeader
                )}
              >
                {typeof title === 'string' ? (
                  <DrawerTitle>{title}</DrawerTitle>
                ) : (
                  title
                )}
                {typeof description === 'string' ? (
                  <DrawerDescription>{description}</DrawerDescription>
                ) : (
                  description
                )}
              </DrawerHeader>
            ) : null}
            <div
              className={cn(
                '~flex-1 ~min-h-0 ~overflow-hidden',
                classNames.drawerContentWrapper
              )}
            >
              {children}
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          '~outline-none ~border-0 focus:~outline-none focus:~ring-0 focus:~ring-offset-0 ~z-[1050]',
          classNames.dialogContent
        )}
        closeClassName={cn(
          'focus:~outline-none focus:~ring-0 focus:~ring-offset-0',
          classNames.dialogClose
        )}
        onInteractOutside={onInteractOutside}
        onPointerDownOutside={onPointerDownOutside}
        onOpenAutoFocus={onOpenAutoFocus}
        onEscapeKeyDown={onEscapeKeyDown}
        overlayClassName={cn('~z-[1040]', classNames.overlayClassName)}
      >
        {title ? (
          <DialogHeader
            className={cn(
              '~flex ~flex-col ~items-center',
              classNames.dialogHeader
            )}
          >
            {typeof title === 'string' ? (
              <DialogTitle>{title}</DialogTitle>
            ) : (
              title
            )}
            {typeof description === 'string' ? (
              <DialogDescription>{description}</DialogDescription>
            ) : (
              description
            )}
          </DialogHeader>
        ) : null}
        <div className={classNames.dialogContentWrapper}>{children}</div>
      </DialogContent>
    </Dialog>
  )
}
